<script setup>
import { ref, computed, onMounted} from 'vue'
import { Icon, Select, Tabs, Tab, Checkbox, ProfilePicture } from '@components'
import { getPrice, sortedDays } from '@utils'
import { Line, Bar, Doughnut } from 'vue-chartjs'

import { useRoute } from 'vue-router'
const route = useRoute()

import { useAppointment } from '@store/appointment'
const $appointment = useAppointment()

import { useCustomer } from '@store/customer'
const $customer = useCustomer()

import { useService } from '@store/service'
const $service = useService()

import { useStaff } from '@store/staff'
const $staff = useStaff()

import { useTransaction } from '@store/transaction'
const $transaction = useTransaction()

import { useSettings } from '@store/settings'
const $settings = useSettings()

import { useGeneral } from '@store/general'
const $general = useGeneral()

import { useServiceItem } from '@store/serviceItem'
const $serviceItem = useServiceItem()

import { useOnboarding } from '@store/onboarding'
const $onboarding = useOnboarding()

import moment from "moment"


const periods = ['Today', 'Monthly', 'Yearly']
const period = ref('Yearly')
const rightSidebar = ref(false)

const setPeriod = (item) => {
    period.value = item
}


// current Time 
const currentTime = ref(moment().format('h:mm A'))
onMounted(async () => {
    $settings.fetchSettings()
    $appointment.fetchData()
    $service.fetchData()
    $customer.fetchCustomers()
    $transaction.fetchTransactions()
    await $staff.fetchData()

    setInterval(() => {
        currentTime.value = moment().format('h:mm A')
    }, 30000)
})

const periodicAppointments = computed(() => {
    console.log('periodicAppointments')
    if (!$appointment.getAppointments) return []

    const compares = {
        Today: 'day',
        Monthly: 'month',
        Yearly: 'year',
    }

    const filteredAppointments = $appointment.getAppointments.filter((app) => {
        if (!['approved', 'pending'].includes(app.meta.status)) return false

        const bookingDate = moment(app.booking_date, 'YYYY-MM-DD')
        const compareUnit = compares[period.value || 'Today']

        return bookingDate.isSame(moment(), compareUnit)
    })

    const enrichedAppointments = filteredAppointments.map((ap) => {
        const transactions = $transaction.getTransactions.filter(
            (transaction) => transaction.appointment_id == ap.appointment_id
        ) || []

        const total_paid = transactions.reduce((amount, transaction) => {
            return amount + (transaction.payment_status === 'paid' ? Number(transaction.payment_amount) : 0)
        }, 0)

        const total_due = ap.meta.price - total_paid
        const customer = $customer.getCustomers.find((c) => c.customer_id == ap.customer_id)
        const staff = $staff.getStaffs.find((s) => s.staff_id == ap.staff_id)
        const service = $service.getServices.find((srv) => srv.service_id == ap.service_id)

        return { ...ap, total_paid, total_due, customer, staff, service }
    })

    // sort appointment
    return enrichedAppointments.sort((a, b) => {
        const dateA = moment(`${a.booking_date} ${a.booking_time_start}`, 'YYYY-MM-DD HH:mm')
        const dateB = moment(`${b.booking_date} ${b.booking_time_start}`, 'YYYY-MM-DD HH:mm')
        return dateA.diff(dateB) // Sort in ascending order (earliest first)
    })
})


const onGoingAppointments = computed(() => {
    const now = moment()
    const today = now.format('YYYY-MM-DD')

    return periodicAppointments.value.filter((app) =>
        app.meta.status == 'approved' &&
        app.booking_date == today &&
        moment(app.booking_time_start, 'HH:mm').isSameOrBefore(now, 'minute') &&
        moment(app.booking_time_end, 'HH:mm').isAfter(now, 'minute')
    )
})


// upcoming appointments (excluding ongoing ones)
const upcomingAppointments = computed(() => {
    // console.log('upcoming')
    const now = moment()
    const endOfToday = moment().endOf('day')

    const ongoingIds = new Set(onGoingAppointments.value.map((app) => app.appointment_id))

    return periodicAppointments.value.filter((app) => {
        const bookingDate = moment(app.booking_date, 'YYYY-MM-DD')
        const bookingStartTime = moment(app.booking_time_start, 'HH:mm')

        // exclude ongoing appointments
        if (ongoingIds.has(app.appointment_id)) return false

        if (period.value === 'Today') {
            // for 'Today' appointments from now to the end of the day.
            return bookingDate.isSame(now, 'day') && bookingStartTime.isAfter(now, 'minute')
        } else {
            // for 'Monthly' and 'Yearly'
            // - today's appointments after the current time.
            // - all future day appointments.
            return (
                (bookingDate.isSame(now, 'day') && bookingStartTime.isAfter(now, 'minute')) ||
                bookingDate.isAfter(now, 'day')
            )
        }
    })
})

// extra counters
const pendingAppointmentsCount = computed(() => {
    return periodicAppointments.value.filter(app => app.meta.status == 'pending').length
})

const totalSales = computed(() => {
    return periodicAppointments.value.filter(app => app.meta.status == 'approved').reduce((amount, app) => amount + app.total_paid, 0)
})

let interval
// sync appointment on every minute to update dashboard
function startMinuteSync() {
    const now = new Date()
    const millisecondsUntilNextMinute = (60 - now.getSeconds()) * 1000

    setTimeout(() => {
        // console.log('Minute Changed')
        $appointment.fetchData()

        interval = setInterval(() => {
            // console.log('Minute Changed')
            $appointment.fetchData()
        }, 61000)
    }, millisecondsUntilNextMinute)
}

onMounted(() => {
    startMinuteSync()
})


/**
 * Popular items.
 */
const popularServices = computed(() => {
    return $service.getServices.filter(

        // Get only the services that are published and at least one appointment is made.
        service => service.status == 'publish' && periodicAppointments.value.filter(app => app.service_id == service.service_id).length > 0

    ).map(service => {
        const count = periodicAppointments.value.filter(app => app.service_id == service.service_id).length
        const sales = periodicAppointments.value.filter(app => app.service_id == service.service_id).reduce((amount, app) => amount + app.total_paid, 0)
        return { ...service, count, sales }
    }).sort((a, b) => b.count - a.count).slice(0, 10)
})

const popularStaffs = computed(() => {
    return $staff.getStaffs.filter(
        staff => staff.meta.status == 'publish' && periodicAppointments.value.filter(app => app.staff_id == staff.staff_id).length > 0

    ).map(staff => {
        const count = periodicAppointments.value.filter(app => app.staff_id == staff.staff_id).length
        const sales = periodicAppointments.value.filter(app => app.staff_id == staff.staff_id).reduce((amount, app) => amount + app.total_paid, 0)
        return { ...staff, count, sales }
    }).sort((a, b) => b.count - a.count).slice(0, 10)
})


// Prebook Profile
const prebookProfile = ref(window.prebook_admin.profile || {})

/**
 * User-defined Filters
 */

const salesFilters = ref({
    service: 'all',
    staff: 'all'
})

// Filter Options within the period.
const servicesOptions = computed(() => {
    return [{ label: 'All', value: 'all' }, ...$service.getServices.filter(service => service.status == 'publish').map(service => {
        return {
            label: service.name,
            value: service.service_id
        }
    })]
})

const staffsOptions = computed(() => {
    return [{ label: 'All', value: 'all' }, ...$staff.getStaffs.filter(staff => staff.meta.status == 'publish').map(staff => {
        return {
            label: staff.name,
            value: staff.staff_id
        }
    })]
})

const filteredAppointments = computed(() => {
    return periodicAppointments.value.filter(app => {
        return (salesFilters.value.service == 'all' || app.service_id == salesFilters.value.service) &&
            (salesFilters.value.staff == 'all' || app.staff_id == salesFilters.value.staff)
    })
})

const salesChart = computed(() => {
    const chartData = {
        labels: [],
        datasets: [
            {
                label: '',
                data: [],
                borderColor: '#7F56D9',
                backgroundColor: '#7F56D9',
                fill: 'start',
            }
        ]
    }

    let labels = [], data = []

    switch (period.value) {

        default:
        case 'Today':

            const day = moment().format('dddd')
            const businessHour = window.prebook_admin.settings.business_hours[day]
            let start = moment(businessHour.hours[0].start, 'hh:mm A').get('hour'),
                end = moment(businessHour.hours[businessHour.hours.length - 1].end, 'hh:mm A').get('hour'),
                endMinutes = moment(businessHour.hours[businessHour.hours.length - 1].end, 'hh:mm A').get('minutes')

            if (endMinutes > 0) {
                end += 1
            }

            for (let i = start; i <= end; i++) {
                // appointments count of today.
                labels.push(moment(i, 'HH').format('h A'))
                data.push(filteredAppointments.value.filter(app => moment(app.booking_time_start, 'HH:mm').get('hour') == i).length || 0)
            }

            break

        case 'Monthly':

            let availableDays = []
            for (let day in window.prebook_admin.settings.business_hours) {
                if (window.prebook_admin.settings.business_hours[day].enabled) {
                    availableDays.push(day)
                }
            }

            const days = moment().daysInMonth()

            for (let i = 1; i <= days; i++) {
                // Push only the available days.
                const thatDay = moment().date(i).format('dddd')
                if (availableDays.includes(thatDay)) {
                    labels.push(i)

                    // appointments count
                    data.push(filteredAppointments.value.filter(app => moment(app.booking_date, 'YYYY-MM-DD').date() == i).length || 0)
                }
            }

            break

        case 'Yearly':
            labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

            for (let i = 1; i <= 12; i++) {
                // appointments count
                data.push(filteredAppointments.value.filter(app => moment(app.booking_date, 'YYYY-MM-DD').month() == i).length || 0)
            }
            break

    }

    // Update the chart data.
    chartData.labels = labels
    chartData.datasets[0].data = data

    return chartData
})

const salesChartOptions = ref({
    responsive: true,
    elements: {
        line: {
            tension: 0.4
        }
    },
    maintainAspectRatio: true,
    yAxis: {
        beginAtZero: true
    },
})


const popularDaysChart = computed(() => {
    return {
        labels: [...sortedDays().map(day => day.slice(0, 3))],
        datasets: [
            {
                label: 'Order by day',
                backgroundColor: 'rgb(127 86 217)',
                data: sortedDays().map(day => periodicAppointments.value.filter(app => moment(app.booking_date, 'YYYY-MM-DD').format('dddd') == day).length),
                borderRadius: 5,
                borderWidth: 1
            }
        ]
    }
})

const popularDaysChartOptions = ref({
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.4,

})

const customerChart = computed(() => {
    const data = {
        labels: ['New', 'Returning', 'Non-Returning'],
        datasets: [
            {
                label: `${$settings.labels.customer?.plural}`,
                backgroundColor: ['#7F56D9', '#1570EF', '#EAECF0'],
                // Calculate the count of each type of customers based on the appointments.
                data: [],
                // 50% size
            }
        ]
    }

    // Customers having only one appointment, are considered as new customers.
    const newCustomers = periodicAppointments.value.filter(app => periodicAppointments.value.filter(a => a.customer_id == app.customer_id).length == 1).length
    const returningCustomers = periodicAppointments.value.filter(app => periodicAppointments.value.filter(a => a.customer_id == app.customer_id).length > 1).length
    const nonReturningCustomers = $customer.getCustomers.length - (newCustomers + returningCustomers)

    data.datasets[0].data = [newCustomers, returningCustomers, nonReturningCustomers]

    return data
})

const customerChartOptions = ref({
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1.5,
})



const showRequirements = ref(true)

const requiredFields = computed(() => {
    let fields = [
        {
            label: 'Add business profile',
            subtitle: `Add your business details so ${String($settings.labels.customer?.plural).toLowerCase()} can easily identify you.`,
            checked: ($settings.business.full_logo_url && $settings.business.name && $settings.business.type) ? true : false,
            buttonTxt: 'Add Profile',
            completedButtonTxt: 'Edit Profile',
            buttonLink: { name: 'onboarding.business.info' },
            visibility: $settings.addons.staffs ? true : false,
            buttonClick: () => {
                // $settings.fetchSettings()
                $onboarding.isBusinessChanged = false
            }
        },
        {
            label: 'Add provider details',
            subtitle: `Add your personal details so ${String($settings.labels.customer?.plural).toLowerCase()} can easily identify you.`,
            checked: ($settings.business.provider_first_name && $settings.business.provider_last_name && $settings.business.provider_display_name && $settings.business.email && $settings.business.full_logo_url && $settings.business.type) ? true : false,
            buttonTxt: 'Add Profile',
            completedButtonTxt: 'Edit Profile',
            buttonLink: { name: 'onboarding.business.info' },
            visibility: !$settings.addons.staffs ? true : false,
            buttonClick: () => {
                $settings.fetchSettings()
            }
        },
        {
            label: `Set ${$settings.addons.staffs ? 'business' : 'working'} hours`,
            subtitle: `Set your ${$settings.addons.staffs ? 'business' : 'working'} hours to let ${String($settings.labels.customer?.plural).toLowerCase()} know when you’re available.`,
            checked: prebook_admin.setup.hours_updated,
            buttonTxt: 'Set Hours',
            completedButtonTxt: 'Edit Hours',
            buttonLink: { name: 'onboarding.business.hours' }
        },
        {
            label: `Add a ${String($settings.labels.staff?.singular).toLowerCase()}`,
            subtitle: `Add a ${String($settings.labels.staff?.singular).toLowerCase()} who will provide ${String($settings.labels.service?.plural).toLowerCase()} to your ${String($settings.labels.customer?.plural).toLowerCase()}.`,
            checked: $staff.getStaffs.find(staff => staff.meta.status == 'publish') ? true : false,
            buttonTxt: `Add ${$settings.labels.staff?.singular}`,
            buttonLink: { name: 'onboarding.staff.profile' },
            visibility: $settings.addons.staffs ? true : false
        },
        {
            label: `Create a ${String($settings.labels.service?.singular).toLowerCase()}`,
            subtitle: `Create a ${String($settings.labels.service?.singular).toLowerCase()} you will offer to your ${String($settings.labels.customer?.plural).toLowerCase()}.`,
            checked: $service.getServices.find(service => service.status == 'publish') ? true : false,
            buttonTxt: `Create ${$settings.labels.service?.singular}`,
            buttonLink: { name: 'onboarding.service.general' },
            buttonClick: () => {
                $serviceItem.resetService()
                $serviceItem.mountServiceItem()
            }
        },
    ]


    return fields.filter(field => {
        return !field.hasOwnProperty('visibility') || field.visibility === true
    })
})

const stepCompleted = computed(() => {
    return requiredFields.value.filter(field => field.checked).length || 0
})


const loading = ref(true)

const incompleteAccount = computed(() => {
    if ( prebook_admin.setup.completed ) {
        return false
    }

    return stepCompleted.value == requiredFields.value.length ? false : true
})

const showRequirementsButton = (item)=>{
    if (item.checked && !item.completedButtonTxt) return false
    // if(item.checked) return true

    return true
    // return !item.checked || (item.checked && item.completedButtonTxt)
}

onMounted(async () => {
    setTimeout(() => {
        loading.value = false
    }, 500)

})

const greeting_message = computed(() => {
    // get current time
    let currentHour = moment().hour()

    if (currentHour >= 0 && currentHour < 12) {
        return 'Good Morning'
    } else if (currentHour >= 12 && currentHour < 18) {
        return 'Good Afternoon'
    }

    return 'Good Evening'

})

// AppointmentBar Component.
import AppointmentBar from './AppointmentBar.vue'
import { isSm } from '../../../utilities/mediaQuery';

const servicePrice = ref(null)
const staffPrice = ref(null)

const getMaxWidth = (item, show_unit = true) => {
    if (!item) return
    let max = 0
    Object.values(item).forEach(e => {
        if (e && e.clientWidth > max) max = e.clientWidth
    })
    return show_unit ? `${max}px` : max
}

const servicePriceMaxWidth = ref('auto')
const staffPriceMaxWidth = ref('auto')

const updateWidths = (tab) => {
    // reset
    servicePriceMaxWidth.value = 'auto'
    staffPriceMaxWidth.value = 'auto'

    // set new value
    setTimeout(() => {
        if (tab == 'service') servicePriceMaxWidth.value = getMaxWidth(servicePrice.value)
        if (tab == 'staff') staffPriceMaxWidth.value = getMaxWidth(staffPrice.value)
        console.log(servicePriceMaxWidth.value, staffPriceMaxWidth.value)
    }, 50)
}



// watch([servicePrice, staffPrice], updateWidths)

const toggleRightSidebar = () => {
    rightSidebar.value = !rightSidebar.value

    if(isSm) return
    if (rightSidebar.value) {
        $general.collapseSideMenu(true)
    } else {
        $general.collapseSideMenu(false)
    }
}
</script>

<template>
    <!-- loader -->
    <div v-if="loading" class="my-[-60px] mx-[-20px] flex"> 
        <div :style="incompleteAccount ? 'max-width: 920px; margin:0 auto;' : rightSidebar ? 'width: calc(100% - 592px) ' : 'width: 100%'" class="overflow-y-hidden py-[38px]" :class="incompleteAccount ? 'px-2' : 'px-[66px]'">
            <div v-if="incompleteAccount">
                <div class="w-[167px] h-[167px] bg-[#EAECF0] rounded-md motion-safe:animate-pulse"></div>

                <div class="flex items-end justify-between gap-5 mt-[25px]">
                    <div class="w-full">
                        <div class="h-[38px] w-[25%] bg-[#EAECF0] rounded-md motion-safe:animate-pulse"></div>
                        <div class="h-[20px] w-[50%] bg-[#EAECF0] rounded-md motion-safe:animate-pulse mt-2"></div>
                    </div>
                    <div class="w-[36px] h-[36px] rounded-full motion-safe:animate-pulse bg-[#EAECF0]"></div>
                </div>

                <div v-if="showRequirements" class="mt-[20px] border border-[#EAECF0] rounded-[10px] p-5">
                    <div class="flex items-center justify-between gap-5">
                        <div class="h-[20px] w-[30%] bg-[#EAECF0] rounded-md motion-safe:animate-pulse"></div>
                        <div class="h-[20px] w-[15%] bg-[#EAECF0] rounded-md motion-safe:animate-pulse"></div>
                    </div>
                    <div class="h-[1px] w-full bg-[#EAECF0] my-5"></div>
                    <div class="flex flex-col items-center gap-4">
                        <div v-for="n in 4" class="bg-[#EAECF0] rounded-lg h-[80px] w-full motion-safe:animate-pulse"></div>
                    </div>
                </div>
            </div>
            <div v-if="!incompleteAccount" class="flex items-center justify-between gap-4 mb-5">
                <div class="h-[38px] bg-[#EAECF0] rounded-md motion-safe:animate-pulse w-[30%]"></div>
                <div class="h-[38px] bg-[#EAECF0] rounded-md motion-safe:animate-pulse w-[15%]"></div>
            </div>
            <div v-if="!incompleteAccount" class="h-[1px] w-full bg-[#EAECF0]"></div>
            <div class="mt-[30px]">
                <div class="h-[28px] w-[20%] bg-[#EAECF0] rounded-md motion-safe:animate-pulse mb-5"></div>
                <div class="flex gap-5">
                    <div v-for="n in 3" class="w-full h-[100px] bg-[#EAECF0] rounded-md motion-safe:animate-pulse"></div>
                </div>
            </div>
            <div class="border-[1px] rounded-xl p-5 mt-[30px]">
                <div class="h-[28px] w-[20%] bg-[#EAECF0] rounded-md motion-safe:animate-pulse mb-5"></div>
                <div class="h-[217px] w-full bg-[#EAECF0] rounded-md motion-safe:animate-pulse"></div>
            </div>


            <div class="flex gap-5">
                <div v-for="n in 2" class="border-[1px] rounded-xl p-5 mt-[30px] w-[50%]">
                    <div class="h-[28px] w-[20%] bg-[#EAECF0] rounded-md motion-safe:animate-pulse mb-5"></div>
                    <div class="bg-[#EAECF0] text-[#EAECF0] h-[200px] rounded-md motion-safe:animate-pulse">Your search “Landing page design” did not match any projects. Please try again.</div>
                </div>
            </div>
        </div>
        <!-- right content -->
        <div v-if="!incompleteAccount" :class="rightSidebar ? 'px-8 w-[592px]' : 'px-[14px] w-[109px]'" class="relative py-8 bg-[#F9FAFB]  border-l border-[#EAECF0]">
            <div v-for="o in 2" class="mb-[32px]">
                <div class="h-[28px] w-full bg-[#EAECF0] rounded-md motion-safe:animate-pulse mb-5"></div>
                <div class="flex flex-col items-center gap-4">
                    <div v-for="n in 2" class=" w-[48px] h-[48px] rounded-full bg-[#EAECF0] motion-safe:animate-pulse"></div>
                </div>
            </div>
        </div>
    </div>


    <!-- content -->
    <div v-if="!loading" class="my-[-60px] mx-[-20px] flex"> 
        <!-- left content -->
        <div :style="incompleteAccount ? 'max-width: 920px; margin:0 auto;' : rightSidebar ? 'width: calc(100% - 592px) ' : 'width: 100%'" class="overflow-y-hidden py-2 md:py-[38px] " :class="incompleteAccount ? 'px-2' : 'px-2 md:px-[66px]'">
            <div v-if="incompleteAccount">
                <div class="w-[167px] h-[167px] rounded-md">
                    <img :src="Image('onboard.png')" class="w-full h-full" alt="Onboarding Image">
                </div>

                <div class="flex items-end justify-between gap-1 md:gap-5 mt-[25px]">
                    <div class="w-full space-y-1.5">
                        <div class="text-[#101828] text-[20px] md:text-[30px] font-semibold capitalize">{{ greeting_message }}, {{ prebookProfile?.name }}</div>
                        <div class="text-[#475467] text-xs md:text-sm">Complete these quick steps to set up your profile and start scheduling {{ String($settings.labels.appointment?.plural).toLowerCase() }}.</div>
                    </div>
                    <Icon @click="showRequirements = !showRequirements" :name="showRequirements ? 'chevronDown' : 'chevronUp'" size="36px" class="text-[#344054] transition-all duration-300 cursor-pointer p-1 border border-[#D0D5DD] rounded-full"></Icon>
                </div>

                <div v-if="showRequirements" class="mt-[20px] border border-[#EAECF0] rounded-[10px] p-5">
                    <div class="flex flex-col items-center justify-between gap-5 md:flex-row">
                        <div class="text-[#101828] text-sm font-semibold">Setup your {{ $settings.addons.staffs ? 'business' : 'profile' }}</div>
                        <div class="flex items-center gap-6 text-sm">
                            <div class="h-[8px] rounded-lg bg-[#EAECF0] w-[113px] overflow-hidden">
                                <div class="bg-[#7F56D9] h-full rounded-lg" :style="`width: ${stepCompleted * 100 / requiredFields.length}%`"></div>
                            </div>
                            <div>{{ stepCompleted }}/{{ requiredFields.length }} completed</div>
                        </div>
                    </div>
                    <div class="h-[1px] w-full bg-[#EAECF0] my-5"></div>
                    <div>
                        <div v-for="item in requiredFields" class="flex gap-4 items-center py-[15px] md:px-5 transition-all duration-300 hover:bg-[#F9FAFB] hover:border-[#F2F4F7] border border-transparent rounded-lg">
                            <Checkbox :checked="item.checked" checkboxClass="!rounded-full" design="secondary"></Checkbox>
                            <div>
                                <div class="text-base font-medium" :class="item.checked ? 'text-[#98A2B3]' : 'text-[#101828]'">{{ item.label }}</div>
                                <div class="mt-1 text-xs md:text-sm" :class="item.checked ? 'text-[#98A2B3]' : 'text-[#475467]'">{{ item.subtitle }}</div>
                            </div>
                            <router-link 
                                v-if="showRequirementsButton(item)" 
                                @click="item.buttonClick && item.buttonClick()" 
                                :to="item.buttonLink" 
                                class="whitespace-nowrap ml-auto px-[14px] py-2 rounded-lg border border-[#D0D5DD] shadow-[0px_1px_2px_0px_#1018280D] text-[#344054] text-sm font-semibold transition-all duration-300 hover:bg-[#7F56D9] hover:border-[#7F56D9] hover:text-white"
                            >
                                {{ item.checked ? item.completedButtonTxt : item.buttonTxt }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!incompleteAccount" class="flex flex-col items-center justify-between gap-4 mb-5 md:flex-row">
                <div class="text-[#101828] text-[20px] md:text-[30px] font-semibold capitalize">{{ greeting_message }}, {{ prebookProfile?.name }}</div>
                <div class="flex" :class="{ 'flex-row-reverse': $settings.isRTL }">
                    <div v-for="item in periods" @click="setPeriod(item)" class="border-l last:border-r first:rounded-l-lg last:rounded-r-lg  border-[#D0D5DD] py-[10px] px-[16px] font-semibold border-y cursor-pointer transition-all  hover:bg-[#F9FAFB] hover:text-[#101828]" :class="[period == item ? 'bg-[#F9FAFB] text-[#101828]' : 'text-[#475467]']">
                        {{ item == 'Yearly' ? 'This Year' : item == 'Monthly' ? 'This Month' : 'Today' }}
                    </div>
                </div>
            </div>
            <div v-if="!incompleteAccount" class="h-[1px] w-full bg-[#EAECF0]"></div>
            <div class="mt-[30px]">
                <div class="text-[#101828] text-lg font-semibold mb-5 capitalize">{{ $settings.labels.appointment?.plural }} Overview</div>
                <div class="grid grid-cols-2 gap-5 md:flex">
                    <div class="w-full py-4 px-5 rounded-lg border-2 border-[#EAECF0] transition-all duration-300 hover:border-[#7F56D9]">
                        <div class="text-[#101828] text-sm font-medium">Total</div>
                        <div class="text-[#101828] text-[30px] font-semibold mt-2">{{ periodicAppointments.length }}</div>
                    </div>
                    <div class="w-full py-4 px-5 rounded-lg border-2 border-[#EAECF0] transition-all duration-300 hover:border-[#7F56D9]">
                        <div class="text-[#101828] text-sm font-medium">Upcoming</div>
                        <div class="text-[#101828] text-[30px] font-semibold mt-2">{{ upcomingAppointments.length }}</div>
                    </div>
                    <div class="w-full py-4 px-5 rounded-lg border-2 border-[#EAECF0] transition-all duration-300 hover:border-[#7F56D9]">
                        <div class="text-[#101828] text-sm font-medium">Pending</div>
                        <div class="text-[#101828] text-[30px] font-semibold mt-2">{{ pendingAppointmentsCount }}</div>
                    </div>
                    <div v-if="$settings.addons.payment" class="w-full py-4 px-5 rounded-lg border-2 border-[#EAECF0] transition-all duration-300 hover:border-[#7F56D9]">
                        <div class="text-[#101828] text-sm font-medium">Sales</div>
                        <div class="text-[#101828] text-[30px] font-semibold mt-2">{{ getPrice(totalSales) }}</div>
                    </div>
                </div>
            </div>

            <!-- appointment graph  -->
            <div class="border-[1px] rounded-xl p-5 mt-[30px]">
                <div class="text-[#101828] text-lg font-semibold mb-5 capitalize">{{ $settings.labels.appointment?.plural }} Graph</div>
                <div v-if="periodicAppointments.length">
                    <div class="flex gap-4 mt-5">
                        <Select :options="servicesOptions" v-model="salesFilters.service" search :searchPlaceholder="`Select ${$settings.labels.service?.singular}`" selectionContainer="py-[10px] px-[16px] w-full font-semibold" class="w-[50%]"></Select>
                        <Select v-if="$settings.addons.staffs" :options="staffsOptions" v-model="salesFilters.staff" search :searchPlaceholder="`Select ${$settings.labels.staff?.singular}`" selectionContainer="py-[10px] px-[16px] w-full font-semibold" class="w-[50%]"></Select>
                    </div>
                    <div class="text-[#7F56D9] textbase font-medium mt-5">{{ periodicAppointments.length }} total {{ String($settings.labels.appointment?.plural).toLowerCase() }}</div>
                    <!-- <div class="mt-5 h-[217px] w-full bg-[#F9FAFB] flex items-center justify-center">
                    Graph comming soon
                </div> -->
                    <Line :data="salesChart" :options="salesChartOptions" />
                </div>
                <div v-else class="max-w-[352px] text-center m-auto">
                    <Icon name="emptyState01" width="152px" height="118px"></Icon>
                    <div class="mt-4 text-[#475467] text-base font-semibold">No sales found</div>
                    <div class="text-[#475467] text-sm mt-[15px]">No sales records found for this period. Please check your filters or wait for new {{ String($settings.labels.appointment?.plural).toLowerCase() }}.</div>
                </div>
            </div>

            <div class="border-[1px] rounded-xl p-3 md:p-5 mt-[30px]">
                <div class="text-[#101828] text-lg font-semibold mb-5">Most Popular</div>
                <Tabs default="service" @update:tab="value => updateWidths(value)">
                    <Tab id="service">{{ $settings.labels.service?.singular }}</Tab>
                    <Tab id="staff" v-if="$settings.addons.staffs">{{ $settings.labels.staff?.singular }}</Tab>
                    <!-- <Tab id="package">Package</Tab> -->

                    <!-- service tab -->
                    <template #service>
                        <div class="flex flex-col gap-2 md:gap-5">
                            <div v-if="popularServices.length" v-for="(service, index) in popularServices" class="flex items-center justify-between w-full h-[30px] md:h-[56px] md:py-2 md:pr-4">
                                <div class="flex items-center">
                                    <div class="font-semibold text-sm md:text-lg text-[#98A2B3]">#{{ index + 1 }}</div>
                                    <div class="flex items-center gap-2 w-full" :class="[$settings.isRTL ? 'mr-4' : 'ml-4']">
                                        <div class="font-semibold text-sm md:text-lg text-[#344054]">{{ service.name }}</div>
                                    </div>
                                </div>
                                <div class="flex items-center">
                                    <div class="flex items-center gap-2" :title="`Order Count ${service.count}`">
                                        <Icon name="package01" size="20px" class="text-[#475467]"></Icon>
                                        <div class="text-[#101828] font-semibold text-sm md:text-lg">{{ service.count }}</div>
                                    </div>
                                    <div v-if="$settings.addons.payment" class="w-[1px] h-7 bg-[#EAECF0] mx-5"></div>
                                    <div v-if="$settings.addons.payment" ref="servicePrice" class="text-[#101828] font-semibold text-sm md:text-lg" :style="`width:${servicePriceMaxWidth}`">{{ getPrice(service.sales) }}</div>
                                </div>
                            </div>
                            <div v-else class="max-w-[352px] text-center m-auto">
                                <Icon name="emptyState01" width="152px" height="118px"></Icon>
                                <div class="mt-4 text-[#475467] text-base font-semibold">No {{ String($settings.labels.service?.plural).toLowerCase() }} found</div>
                                <div class="text-[#475467] text-sm mt-[15px] text-wrap">No popular {{ String($settings.labels.service?.plural).toLowerCase() }} found for this period. Check back later or adjust filters to see popular {{ String($settings.labels.service?.plural).toLowerCase() }}.</div>
                            </div>
                        </div>
                    </template>

                    <!-- staff tab -->
                    <template #staff>
                        <div class="flex flex-col gap-2 md:gap-5">
                            <div v-if="popularStaffs.length" v-for="(staff, index) in popularStaffs" class="flex items-center justify-between w-full h-[30px] md:h-[56px] md:py-2 md:pr-4">
                                <div class="flex items-center">
                                    <div class="font-semibold text-sm md:text-lg text-[#98A2B3]">#{{ index + 1 }}</div>
                                    {{ $settings.isRTl }}
                                    <div class="flex items-center w-full gap-2" :class="[$settings.isRTL ? 'mr-4' : 'ml-4']">
                                        <!-- <div :style="`background-image: url('${staff.meta?.profile_image_url}')`" class="w-10 h-10 bg-center bg-no-repeat bg-cover rounded-full"></div> -->
                                        <ProfilePicture :name="staff?.name" :img="staff?.meta?.profile_image_url" :bgColor="staff?.meta?.color" class="w-5 h-5 md:w-10 md:h-10" />
                                        <div class="font-semibold text-sm md:text-lg text-[#344054]">{{ staff.name }}</div>
                                    </div>
                                </div>
                                <div class="flex items-center transition-all">
                                    <div class="flex items-center gap-2" :title="`Order Count ${staff.count}`">
                                        <Icon name="package01" size="20px" class="text-[#475467]"></Icon>
                                        <div class="text-[#101828] font-semibold text-sm md:text-lg">{{ staff.count }}</div>
                                    </div>
                                    <div v-if="$settings.addons.payment" class="w-[1px] h-7 bg-[#EAECF0] mx-5"></div>
                                    <div v-if="$settings.addons.payment" ref="staffPrice" :style="`width:${staffPriceMaxWidth}`" class="text-[#101828] font-semibold text-sm md:text-lg">{{ getPrice(staff.sales) }}</div>
                                </div>
                            </div>
                            <div v-else class="max-w-[352px] text-center m-auto">
                                <Icon name="emptyState01" width="152px" height="118px"></Icon>
                                <div class="mt-4 text-[#475467] text-base font-semibold">No {{ String($settings.labels.staff?.singular).toLowerCase() }} found</div>
                                <div class="text-[#475467] text-sm mt-[15px] text-wrap">No {{ String($settings.labels.staff?.singular).toLowerCase() }} members stand out this period. Adjust filters or wait for more bookings to see popular {{ String($settings.labels.staff?.singular).toLowerCase() }}.</div>
                            </div>
                        </div>
                    </template>

                    <!-- package tab -->
                    <!-- <template #package>
                        <div class="text-center text-[#b84646]">No package found</div>
                    </template> -->
                </Tabs>
            </div>
            <div class="flex flex-col gap-5 md:flex-row">
                <div class="border-[1px] rounded-xl p-5 mt-[30px] md:w-[50%]">
                    <div class="text-[#101828] text-lg font-semibold mb-5">Popular Days Of The Week</div>
                    <Bar v-if="periodicAppointments.length" :data="popularDaysChart" :options="popularDaysChartOptions" />
                    <div v-else class="w-full m-auto text-center">
                        <Icon name="emptyState01" width="152px" height="118px"></Icon>
                        <div class="mt-4 text-[#475467] text-base font-semibold">No days found</div>
                        <div class="text-[#475467] text-sm mt-[15px] text-wrap">No popular booking days yet. Bookings for this period might still come in. Adjust filters for other timeframes.</div>
                    </div>
                </div>
                <div class="border-[1px] rounded-xl p-5 md:mt-[30px] md:w-[50%]">
                    <div class="text-[#101828] text-lg font-semibold mb-5 capitalize">{{ $settings.labels.customer?.singular }}</div>
                    <Doughnut v-if="periodicAppointments.length" :data="customerChart" :options="customerChartOptions" />
                    <div v-else class="w-full m-auto text-center">
                        <Icon name="emptyState01" width="152px" height="118px"></Icon>
                        <div class="mt-4 text-[#475467] text-base font-semibold">No {{ String($settings.labels.customer?.plural).toLowerCase() }} found</div>
                        <div class="text-[#475467] text-sm mt-[15px] text-wrap">No {{ String($settings.labels.customer?.singular).toLowerCase() }} records found for this period. Please check your filters or wait for new {{ String($settings.labels.customer?.singular).toLowerCase() }} registrations.</div>
                    </div>
                </div>
            </div>
        </div>
        <!-- right content -->
        <div v-if="!incompleteAccount && $settings.addons.staffs  && !isSm" :class="rightSidebar ? 'p-2 md:px-8 w-full md:w-[592px]' : 'md:px-[14px] md:w-[109px] py-0 px-0 w-0'" class="relative md:py-8 bg-[#F9FAFB] border-l border-[#EAECF0]">
            <!-- <Icon :name="rightSidebar ? 'chevronLeftDouble' : 'chevronRightDouble'" size="36px" class="cursor-pointer absolute top-[32px] left-[-20px] text-[#344054] p-2 rounded-full border-[1px] border-[#D0D5DD] bg-white shadow-[0px_1px_2px_0px_#1018280D]"></Icon> -->
            <Icon @click="toggleRightSidebar()" name="chevronLeftDouble" size="36px" :class="`${rightSidebar == true && !$settings.isRTL ? 'rotate-180' : ''} ${rightSidebar == false && $settings.isRTL ? 'rotate-180' : ''} ${$settings.isRTL ? 'right-[-18px]' : 'left-[-19px]'} transition-all duration-500 cursor-pointer absolute top-[32px] text-[#344054] p-2 rounded-full border-[1px] border-[#D0D5DD] bg-white shadow-[0px_1px_2px_0px_#1018280D]`" />
            <div class="mb-[32px] overflow-hidden">

                <!-- Ongoing -->
                <div v-if="onGoingAppointments.length" class=" mb-5">
                    <div class="font-semibold text-base text-[#344054] mb-5 flex items-center gap-4" :class="!rightSidebar ? 'justify-center' : ''">Ongoing <span v-if="rightSidebar">{{ currentTime }}</span></div>
                    <div class="flex flex-col gap-4">
                        <AppointmentBar v-for="appointment in onGoingAppointments" :key="appointment.appointment_id" :appointment="appointment" :rightSidebar="rightSidebar" :moment="moment" :route="route" />
                    </div>
                </div>

                <!-- Upcoming  -->
                <div v-if="upcomingAppointments.length">
                    <div class="font-semibold text-base text-[#344054] mb-5 flex items-center gap-4" :class="!rightSidebar ? 'justify-center' : ''">Upcoming</div>
                    <div class="flex flex-col gap-4">
                        <AppointmentBar v-for="appointment in upcomingAppointments" :key="appointment.appointment_id" :appointment="appointment" :rightSidebar="rightSidebar" :moment="moment" :route="route" />
                    </div>
                </div>

                <!-- When nothing is here  -->
                <div v-if="!onGoingAppointments.length && !upcomingAppointments.length" class="m-auto text-center" :class="rightSidebar ? 'max-w-[352px]' : 'hidden md:block md:max-w-[109px]'">
                    <Icon v-if="rightSidebar" name="emptyState01" width="152px" height="118px"></Icon>
                    <div class="mt-4 text-[#475467] text-base font-semibold " :class="{
                        'whitespace-pre-wrap text-left text-xs leading-5 mt-14': !rightSidebar
                    }">No {{ String($settings.labels.appointment?.plural).toLowerCase() }} found</div>
                    <div v-if="rightSidebar" class="text-[#475467] text-sm mt-[15px]">No {{ String($settings.labels.appointment?.plural).toLowerCase() }} today! Wait for new {{ String($settings.labels.appointment?.plural).toLowerCase() }} or create a manual {{ String($settings.labels.appointment?.plural).toLowerCase() }} for your {{ String($settings.labels.customer?.plural).toLowerCase() }}.</div>

                </div>
            </div>
        </div>
    </div>
</template>

<style>

</style>